/**
 * Pure helpers behind MeetingCommitments.jsx — the "From your meetings" card
 * on the Tasks page (cs-analysis docs/design-meeting-commitments.md §3). The
 * api's /meeting-commitments/mine rows are grouped by meeting so one call
 * reads as one block, and the copy of the card is derived here so it can be
 * tested without a backend.
 */

const DAY_MS = 86400000;

/** Offers grouped by meeting (client + meeting time), newest meeting first; offers inside keep the api's order. */
export function group_offers_by_meeting(offers) {
    const groups = new Map();
    (Array.isArray(offers) ? offers : []).forEach((o) => {
        if (!o || !o.id) return;
        const key = `${o.clientId || ''}|${o.meetingAt || ''}`;
        if (!groups.has(key)) {
            groups.set(key, { key, clientId: o.clientId || null, storeId: o.storeId || null, clientName: o.clientName || 'Unknown client', meetingAt: o.meetingAt || null, offers: [] });
        }
        groups.get(key).offers.push(o);
    });
    return [...groups.values()].sort((a, b) => String(b.meetingAt || '').localeCompare(String(a.meetingAt || '')));
}

/** "expires in 3 days" / "expires today" / "expired" — from the api's expiresAt. */
export function expiry_label(expires_at, now = Date.now()) {
    const t = expires_at ? new Date(expires_at).getTime() : NaN;
    if (Number.isNaN(t)) return '';
    const days = Math.ceil((t - now) / DAY_MS);
    if (days <= 0) return 'expired';
    if (days === 1) return 'expires today';
    return `expires in ${days} days`;
}

/** Trims a long quote for the card; the full quote is the title attribute. */
export function short_quote(quote, max = 220) {
    const q = String(quote || '').replace(/\s+/g, ' ').trim();
    return q.length > max ? q.slice(0, max - 1).trimEnd() + '…' : q;
}

/** What the card says after a decision (the api's outcome), for the small toast line. */
export function decision_message(action, outcome) {
    if (outcome && outcome.kind === 'already') {
        if (outcome.status === 'CREATED') return 'Already on your tasks.';
        if (outcome.status === 'IGNORED') return 'Already dropped.';
        return 'This offer had already expired.';
    }
    return action === 'accept' ? 'Added to your tasks.' : 'Dropped — nothing was created.';
}

/** The offers left after a decision: the decided one leaves the list whatever the outcome. */
export function without_offer(offers, id) {
    return (Array.isArray(offers) ? offers : []).filter((o) => o && o.id !== id);
}

/**
 * Whether the Tasks page mounts the "From your meetings" card: on the Tasks
 * filter, in the rep's own scope — or for an admin in any scope. An admin who
 * is not a CS rep sees only the "All" scope (no "My" tab), and the card's rep
 * picker is how they reach a rep's offers; gating on scope === 'my' alone hid
 * it from exactly that admin (found on prod 2026-09-23).
 */
export function show_meetings_card({ item_filter, scope, is_admin }) {
    return item_filter === 'actionables' && (scope === 'my' || !!is_admin);
}
